import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEdit, faTrash, faUser, faClock, faNotesMedical, faStethoscope } from '@fortawesome/free-solid-svg-icons';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { getStatusColorDot, APPOINTMENT_STATUSES, getStatusLabel } from '../../utils/appointmentStatus';
import { getAppointmentNotes, type Appointment, type PatientTag } from '../../services/clinicsService';

interface ClinicsAppointmentPopoverProps {
  appointment: Appointment;
  position: { x: number; y: number };
  onClose: () => void;
  onEdit: (appointment: Appointment) => void;
  onDelete: (appointment: Appointment) => void;
  onTagsChange: (appointmentId: number, tagIds: number[]) => void;
  onStatusChange: (appointmentId: number, status: string) => void;
  availableTags: PatientTag[];
  tagsEnabled?: boolean;
  onViewPatient: (patientId: number) => void;
}

const ClinicsAppointmentPopover: React.FC<ClinicsAppointmentPopoverProps> = ({
  appointment,
  position,
  onClose,
  onEdit,
  onDelete,
  onTagsChange,
  onStatusChange,
  availableTags,
  tagsEnabled = true,
  onViewPatient
}) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    if (popoverRef.current) {
      const rect = popoverRef.current.getBoundingClientRect();
      let newX = position.x;
      let newY = position.y;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Adjust X
      if (newX + rect.width > viewportWidth - 20) {
        newX = viewportWidth - rect.width - 20;
      }
      
      // Adjust Y (Render upwards if bottom space is insufficient)
      if (newY + rect.height > viewportHeight - 20) {
        newY = position.y - rect.height - 10; // Open upwards
      } else {
         newY = position.y + 10; // Open slightly below
      }

      setAdjustedPosition({ x: Math.max(20, newX), y: Math.max(20, newY) });
    }
  }, [position]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        popoverRef.current && 
        !popoverRef.current.contains(target) &&
        !target.closest('[data-ghost-dropdown="true"]')
      ) {
        onClose();
      }
    };

    const handleScroll = (event: Event) => {
      const target = event.target as Element;
      if (
        popoverRef.current && 
        !popoverRef.current.contains(target) &&
        !target.closest('[data-ghost-dropdown="true"]')
      ) {
        onClose();
      }
    };

    // Use capturing phase for scroll to catch it early
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const appDate = parseISO(appointment.start_time);
  const endTime = parseISO(appointment.end_time);
  const notes = getAppointmentNotes(appointment);

  return createPortal(
    <div
      data-ui="clinics-appointment-popover"
      ref={popoverRef}
      className="page-theme-surface page-theme-clinics fixed z-[9999] bg-black/80 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 w-80 animate-fade-in"
      style={{ left: adjustedPosition.x, top: adjustedPosition.y }}
    >
      <div data-ui="clinics-appointment-popover-region-001" className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-bold text-white pr-6">
          Detalles de Cita
        </h3>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="text-white/50 hover:text-white transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div data-ui="clinics-appointment-popover-region-002" className="space-y-3">
        {/* Time */}
        <div data-ui="clinics-appointment-popover-region-003" className="flex items-start gap-3">
          <div data-ui="clinics-appointment-popover-region-004" className="w-5 flex justify-center text-blue-400 mt-1">
             <FontAwesomeIcon icon={faClock} />
          </div>
          <div data-ui="clinics-appointment-popover-region-005">
            <div data-ui="clinics-appointment-popover-region-006" className="text-white font-medium capitalize">
              {format(appDate, 'EEEE, d MMMM', { locale: es })}
            </div>
            <div data-ui="clinics-appointment-popover-region-007" className="text-white/70 text-sm">
              {format(appDate, 'HH:mm')} - {format(endTime, 'HH:mm')}
            </div>
          </div>
        </div>

        {/* Patient */}
        <div data-ui="clinics-appointment-popover-region-008" className="flex items-start gap-3">
          <div data-ui="clinics-appointment-popover-region-009" className="w-5 flex justify-center text-blue-400 mt-1">
             <FontAwesomeIcon icon={faUser} />
          </div>
          <div data-ui="clinics-appointment-popover-region-010">
            <div data-ui="clinics-appointment-popover-region-011" className="text-white font-medium">
              {appointment.patient_name || `Paciente #${appointment.patient_id}`}
            </div>
            <button 
              onClick={() => { onClose(); onViewPatient(appointment.patient_id); }}
              className="text-blue-400 hover:text-blue-300 text-xs mt-1 underline"
            >
              Ver perfil del paciente
            </button>
          </div>
        </div>

        {/* Doctor */}
        {appointment.doctor_name && (
          <div data-ui="clinics-appointment-popover-region-012" className="flex items-start gap-3">
            <div data-ui="clinics-appointment-popover-region-013" className="w-5 flex justify-center text-blue-400 mt-1">
              <FontAwesomeIcon icon={faStethoscope} />
            </div>
            <div data-ui="clinics-appointment-popover-region-014" className="text-white/90 text-sm mt-0.5">
              {appointment.doctor_name}
            </div>
          </div>
        )}

        {/* Type & Notes */}
        <div data-ui="clinics-appointment-popover-region-015" className="flex items-start gap-3">
          <div data-ui="clinics-appointment-popover-region-016" className="w-5 flex justify-center text-blue-400 mt-1">
            <FontAwesomeIcon icon={faNotesMedical} />
          </div>
          <div data-ui="clinics-appointment-popover-region-017" className="text-white/90 text-sm mt-0.5">
            <div data-ui="clinics-appointment-popover-region-018" className="capitalize">{appointment.appointment_type}</div>
            {notes && (
              <div data-ui="clinics-appointment-popover-region-019" className="mt-1 text-white/60 italic text-xs border-l-2 border-white/20 pl-2">
                "{notes}"
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        {tagsEnabled && (
          <div data-ui="clinics-appointment-popover-region-020" className="flex items-center gap-3 pt-2">
            <div data-ui="clinics-appointment-popover-region-021" className="w-5 flex justify-center text-blue-400">
               <div data-ui="clinics-appointment-popover-region-022" className="w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]" />
            </div>
            <div data-ui="clinics-appointment-popover-region-023" className="flex-1">
               <GhostSelect
                 value={appointment.tags?.map(t => String(t.id)) || []}
                 multiple={true}
                 onChange={(val: string[]) => {
                   onTagsChange(appointment.id, val.map(v => parseInt(v, 10)));
                 }}
                 className="w-full text-sm"
                 options={availableTags.map(tag => ({
                   value: String(tag.id),
                   label: (
                     <div data-ui="clinics-appointment-popover-region-024" className="flex items-center gap-2">
                       <div data-ui="clinics-appointment-popover-region-025" className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color || '#888' }} />
                       {tag.name}
                     </div>
                   )
                 }))}
               />
            </div>
          </div>
        )}
        
        {/* Status */}
        <div data-ui="clinics-appointment-popover-region-026" className="flex items-center gap-3 pt-2">
          <div data-ui="clinics-appointment-popover-region-027" className="w-5 flex justify-center" style={{ color: getStatusColorDot(appointment.status || 'scheduled') }}>
             <div data-ui="clinics-appointment-popover-region-028" className="w-2 h-2 rounded-full shadow-[0_0_8px_currentColor]" style={{ backgroundColor: getStatusColorDot(appointment.status || 'scheduled') }} />
          </div>
          <div data-ui="clinics-appointment-popover-region-029" className="flex-1 w-full max-w-full min-w-0">
             <GhostSelect
               value={appointment.status || 'scheduled'}
               onChange={(val: string) => {
                 onStatusChange(appointment.id, val);
               }}
               className="w-full text-sm"
               options={APPOINTMENT_STATUSES.map(status => ({
                 value: status,
                 label: (
                   <div data-ui="clinics-appointment-popover-region-030" className="flex items-center gap-2">
                     <div data-ui="clinics-appointment-popover-region-031" className="w-2 h-2 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: getStatusColorDot(status) }} />
                     <span>{getStatusLabel(status, t)}</span>
                   </div>
                 ),
                 textLabel: getStatusLabel(status, t)
               }))}
             />
          </div>
        </div>
      </div>

      <div data-ui="clinics-appointment-popover-region-032" className="flex gap-2 mt-5 pt-4 border-t border-white/10">
        <button
          onClick={() => { onClose(); onEdit(appointment); }}
          className="flex-1 btn-taratic btn-ghost-dark text-sm py-1.5 flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faEdit} />
          Editar
        </button>
        <button
          onClick={() => { onClose(); onDelete(appointment); }}
          className="flex-1 btn-taratic btn-ghost-dark text-red-400 hover:text-red-300 text-sm py-1.5 flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faTrash} />
          Eliminar
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ClinicsAppointmentPopover;
